import { Container, Spinner } from '@/components';
import { CookieKey, MasterTranslation, SendMailStatusCode, SignUpSource } from '@/constants';
import { sendVerificationEmail } from '@/fetchers';
import { useGlobalState } from '@/hooks';
import { type Breakpoints, OnboardingStepName } from '@/interfaces';
import { PmtSignUpStep } from '@/interfaces/tracking';
import { pushEvent, renderHTML } from '@/utils';
import Cookies from 'js-cookie';
import { useState } from 'react';
import {
  Check,
  DesktopButton,
  DesktopTitle,
  HeaderBannerWrapper,
  MobileButton,
  MobileTitle,
} from './HeaderBanner.styles';

export enum ButtonText {
  RESEND = 'Resend link',
  SENDING = 'Sending link',
  SENT = 'Link sent',
}

const HeaderBanner: React.FC = () => {
  const {
    userInfo,
    showPopUp,
    showPMTPopUp,
    isLoggedIn,
    currentStep,
    verificationData,
    setVerificationData,
    setIsFromBasicDetailsBtn,
    setCurrentStep,
    setShowPopUp,
    setShowPMTPopUp,
    setCurrentPMTSignUpStep,
    t,
    pmtCurrentLanguageCode,
    isMultiLanguagePage,
  } = useGlobalState();
  const browserUuid = Cookies.get(CookieKey.BrowserUuid);
  const userEmail = userInfo?.email || '';
  const isBasicDetails = !!userInfo?.firstName && !!userInfo.lastName && !!userInfo.companyName;
  const isInvalid = verificationData?.isInvalid;

  const [buttonText, setButtonText] = useState(ButtonText.RESEND);

  const isGuest = !userEmail;
  const isUnverified = !isGuest && !isLoggedIn;
  const isFullProcess = !isUnverified && isBasicDetails;
  const isVerifiedFromMultiLanguagePage = !isUnverified && isMultiLanguagePage;

  // Hide this header banner when the popup is opening or process is done
  if (showPopUp || showPMTPopUp || isFullProcess || !userInfo || isVerifiedFromMultiLanguagePage || isGuest)
    return null;

  const isShowOnboardingHeaderBanner =
    !isMultiLanguagePage && userInfo && !isBasicDetails && !isUnverified && currentStep !== OnboardingStepName.finish;

  const commonProps = { breakpoint: (isMultiLanguagePage ? 'lg' : 'md') as keyof Breakpoints };

  // COMPLETE ONBOARDING
  if (isShowOnboardingHeaderBanner) {
    const handleCompleteOnboardingButton = () => {
      setIsFromBasicDetailsBtn(true);
      pushEvent('complete_onboarding_button_clicks'); // TODO-GA3: deprecated
    };

    return (
      <HeaderBannerWrapper role='banner' aria-label='Complete your profile banner' {...commonProps}>
        <Container>
          {/* Mobile content - Only show these content on Mobile, hide on Desktop */}
          <MobileTitle {...commonProps}>Complete your profile</MobileTitle>
          <MobileButton
            {...commonProps}
            onClick={handleCompleteOnboardingButton}
            aria-label='Complete your profile setup'
          >
            Let&apos;s go
          </MobileButton>

          {/* Desktop content - Only show these content on Desktop, hide on Mobile */}
          <DesktopTitle {...commonProps}>You&apos;re almost there! Help us get to know you</DesktopTitle>
          <DesktopButton
            {...commonProps}
            onClick={handleCompleteOnboardingButton}
            aria-label='Complete your profile setup'
          >
            Complete onboarding
          </DesktopButton>
        </Container>
      </HeaderBannerWrapper>
    );
  }

  // EMAIL VERIFICATION
  const handleResendEmail = async () => {
    setButtonText(ButtonText.SENDING);

    try {
      const response = await sendVerificationEmail({
        email: userEmail,
        browserUuid,
        language: pmtCurrentLanguageCode,
        source: isMultiLanguagePage ? SignUpSource.ConsumerWeb : SignUpSource.Insights,
      });

      const hasError = 'error' in response;
      if (hasError) {
        throw response;
      }

      let buttonText = ButtonText.RESEND;

      if (response.status === SendMailStatusCode.Valid) {
        buttonText = ButtonText.SENT;

        if (isMultiLanguagePage) {
          setCurrentPMTSignUpStep(PmtSignUpStep.VerifyEmail);
          setShowPMTPopUp(true);
        } else {
          setCurrentStep(OnboardingStepName.accountVerification);
          setShowPopUp(true);
        }

        if (isInvalid) {
          setVerificationData({ isInvalid: undefined });
        }
      }

      setButtonText(buttonText);
    } catch (error: any) {
      console.error(error);
    } finally {
      setTimeout(() => setButtonText(ButtonText.RESEND), 5000);
    }
  };

  const translatedText = {
    ValidLinkTextDesktop: t(MasterTranslation.ValidLinkTextDesktop),
    ValidLinkTextMobile: t(MasterTranslation.ValidLinkTextMobile),
    InvalidLinkTextDesktop: t(MasterTranslation.InvalidLinkTextDesktop),
    InvalidLinkTextMobile: t(MasterTranslation.InvalidLinkTextMobile),
    ResendLink: t(MasterTranslation.ResendLink),
    SendingLink: t(MasterTranslation.SendingLink),
    Sending: t(MasterTranslation.Sending),
    LinkSent: t(MasterTranslation.LinkSent),
  };

  const renderTitle = ({ isMobile }: { isMobile: boolean }) => {
    switch (true) {
      case isMobile && isInvalid:
        return <MobileTitle {...commonProps}>{translatedText.InvalidLinkTextMobile}</MobileTitle>;

      case isMobile && buttonText === ButtonText.SENT:
        return <MobileTitle {...commonProps}>{translatedText.ValidLinkTextMobile}</MobileTitle>;

      case isMobile:
        return <MobileTitle {...commonProps}>{translatedText.ValidLinkTextMobile}</MobileTitle>;

      case !isMobile && isInvalid:
        return <DesktopTitle {...commonProps}>{translatedText.InvalidLinkTextDesktop}</DesktopTitle>;

      case !isMobile:
        return (
          <DesktopTitle {...commonProps}>
            {renderHTML(`${translatedText.ValidLinkTextDesktop.replace('{email}', `<i>${userEmail}</i>`)}`, 'span')}
          </DesktopTitle>
        );

      default:
        return null;
    }
  };

  const renderButton = ({ isMobile }: { isMobile?: boolean }) => {
    const Button = isMobile ? MobileButton : DesktopButton;

    switch (buttonText) {
      case ButtonText.RESEND:
        return (
          <Button
            {...commonProps}
            truncate={2}
            onClick={handleResendEmail}
            aria-label={`Resend verification email to ${userEmail}`}
            aria-live='polite'
          >
            {translatedText.ResendLink}
          </Button>
        );

      case ButtonText.SENDING:
        return (
          <Button
            {...commonProps}
            hasIcon
            truncate={2}
            disabled
            aria-label={`Sending verification email to ${userEmail}`}
            aria-live='polite'
          >
            <Spinner text={isMobile ? translatedText.Sending : translatedText.SendingLink} />
          </Button>
        );

      case ButtonText.SENT:
        return (
          <Button
            {...commonProps}
            hasIcon
            disabled
            aria-label={`Verification email sent to ${userEmail}`}
            aria-live='polite'
          >
            <Check />
            <span>{translatedText.LinkSent}</span>
          </Button>
        );

      default:
        return null;
    }
  };

  const bannerRole = isInvalid ? 'alert' : 'banner';
  const bannerAriaLabel = isInvalid
    ? 'Email verification error - verification link is invalid'
    : 'Email verification required';

  return (
    <HeaderBannerWrapper id='header-banner' role={bannerRole} aria-label={bannerAriaLabel} {...commonProps}>
      <Container>
        {/* Mobile content - Only show these content on Mobile, hide on Desktop */}
        <>
          {renderTitle({ isMobile: true })}
          {renderButton({ isMobile: true })}
        </>
        {/* Desktop content - Only show these content on Desktop, hide on Mobile */}
        <>
          {renderTitle({ isMobile: false })}
          {renderButton({ isMobile: false })}
        </>
      </Container>
    </HeaderBannerWrapper>
  );
};

export default HeaderBanner;
